import type { Vec2 } from './vec'
import type { Aabb, Slot, Tile } from './types'
import { makeAabb } from './types'
import { makeRngState, subSeed, type RngState } from './rng'
import { placeBuilding, type Command } from './commands'
import {
  extractionSlotCount,
  validateChain,
  type Content,
  type ContentId,
} from '../content/schema'

// The single object holding all game state. Every field is JSON-serializable:
// no classes with engine dependencies, no Maps, no closures. Inventories are
// plain Records and PRNG cursors are plain numbers, so the state serializes
// stably for the determinism check and, later, for saving.

/** Fixed sim step, in seconds. Faster play runs more steps, never a bigger dt. */
export const SIM_DT = 1 / 60

// Fixed sim-world layout for the starter chain. Building positions are part of
// SimState and drive travel distances, so they must not depend on the render
// viewport — a viewport-derived layout would make two clients with different
// window sizes diverge from the same seed + command log. The render adapter
// scales these world coordinates to its canvas; the core owns the coordinates.
const CHAIN_ORIGIN_X = 140
const CHAIN_ROW_Y = 120
const CHAIN_SPACING_X = 200
/** Vertical gap between parallel chain groups. */
const GROUP_ROW_SPACING = 130
const BUILDING_SIZE = 48
/**
 * Access slots sit to the right of the building, so a hauler arriving from the
 * upstream building on the left must round the box to reach them — this makes
 * the obstacle-avoidance in the steering genuinely exercised, not a straight
 * line. Kept larger than the colonist radius so a slot never lands inside the
 * building's non-penetration margin.
 */
const SLOT_GAP = 16
/** Vertical spacing between a building's own access slots. */
const SLOT_PITCH = 16
/** Horizontal spacing of the waiting queue that forms past the access slots. */
const QUEUE_STRIDE = 16
/** Fixed rally point where idle colonists pool, clear of the chain. */
const POOL_POINT: Vec2 = { x: 70, y: 50 }
/** Grid spacing used to fan pooled colonists out so the pool reads at a glance. */
const POOL_STRIDE = 10
const POOL_COLS = 6

// Placement grid. GRID_TILE is the world-unit pitch of one tile step; it divides
// every layout constant above, so the starter chain lands on integer tiles at
// its exact world coordinates. The extent (cols x rows) is fixed sim-space, never
// derived from the render viewport, so a placement resolves to the same tile on
// every client. A tile index maps to the world center of a building placed there.
const GRID_TILE = 10
const GRID_COLS = 100
const GRID_ROWS = 80
/**
 * Tiles above an extractor its resource patch is seeded at. A patch and a
 * building both cover a 5-tile-wide footprint, so a 5-tile center offset leaves
 * the patch orthogonally adjacent to the extractor (footprints touch, one tile
 * apart) without overlapping it.
 */
const PATCH_OFFSET_TILES = 5

/** PRNG sub-stream names. Each system draws from its own stream. */
export const RNG_STREAMS = ['map', 'traits', 'contracts', 'events'] as const
export type RngStreamName = (typeof RNG_STREAMS)[number]

/** Colonist hauling phase. */
export type HaulPhase = 'idle' | 'to_source' | 'to_dest'

/** A moving colonist. Fields match the steering Unit shape. */
export interface SimUnit {
  id: number
  pos: Vec2
  target: Vec2 | null
  radius: number
  speed: number
  phase: HaulPhase
  /** Chain index of the current haul source / dest, or -1 when idle. */
  fromIdx: number
  toIdx: number
  /** Item assigned this task, or null. */
  taskItem: ContentId | null
  /** Item currently carried (capacity 1), or null. */
  cargo: ContentId | null
  /**
   * Chain this colonist serves (index into `state.chains`), or -1 when
   * unassigned and sitting in the shared labor pool at the rally point. Only the
   * assignment arbiter writes it; a unit stays assigned to its chain between
   * hauls (idle at the rally point but still counted against the chain's target).
   */
  chainId: number
}

/**
 * A chain instance: one player-assignable ordered set of buildings plus the
 * target headcount the arbiter draws from the shared pool to serve it. Built
 * once from the `chain` template × `groupCount` at construction. Plain data — an
 * array of building indices and a number, no Map/class — so it serializes.
 * The buildings, their order, and the pull edges over them come from `chain` +
 * recipe data and never change with assignment; only `target` (and which
 * colonists a chain draws) is player-directed, which is why reassigning labor
 * rebuilds no routes.
 */
export interface SimChain {
  /** Flat indices into `state.buildings`, in chain order. */
  buildingIdx: number[]
  /** How many colonists the arbiter should draw from the shared pool. */
  target: number
}

/**
 * A building placed on the grid. Owns its own inventory and access-slot
 * occupancy; other systems submit claims rather than writing it directly.
 * `inventory` is a plain Record so it serializes and compares by value. A
 * building's position in a chain is its flat index in `state.buildings` (set by
 * the order placements drain), not a stored field.
 */
export interface SimBuilding {
  id: ContentId
  box: Aabb
  slots: Slot[]
  /** itemId -> count held. */
  inventory: Record<ContentId, number>
  /** Ticks left in the current production cycle; 0 when idle/ready. */
  cooldown: number
}

/**
 * A resource patch on the map: an infinite, throughput-capped source an adjacent
 * extractor draws from. Plain data (no Map/class/closure) so it serializes and
 * compares by value like SimBuilding. A patch is world/map data, seeded at
 * construction — not player intent — so it has no command and never depletes;
 * `extractionSlots` caps how many extractors may draw from it at once.
 */
export interface SimPatch {
  id: ContentId
  box: Aabb
  item: ContentId
  extractionSlots: number
}

/**
 * The placement grid: a fixed sim-space lattice buildings snap to. `occupied`
 * maps a tile key `${tx},${ty}` to true for every tile a placed building's box
 * covers, so a later placement whose footprint overlaps is rejected. Only the
 * phase-1 place_building drain writes this slice (single writer). Plain data —
 * numbers and a Record — so it serializes and compares by value.
 */
export interface SimGrid {
  /** World units per tile step. */
  tile: number
  /** Placeable extent in tiles. */
  cols: number
  rows: number
  /** Occupied tiles, keyed `${tx},${ty}`. */
  occupied: Record<string, boolean>
}

/** An inventory move: reserved during movement, applied during commit. */
export interface Transfer {
  kind: 'pickup' | 'dropoff'
  unitId: number
  buildingIdx: number
  item: ContentId
}

/** A completed carry leg. */
export interface CompletedLeg {
  from: ContentId
  to: ContentId
  item: ContentId
}

/** Each sub-stream's running PRNG cursor plus the master seed. All numbers. */
export interface SimRng {
  masterSeed: number
  streams: Record<string, RngState>
}

/** The plain-data object owning all game state. */
export interface SimState {
  /** Whole ticks elapsed. */
  tick: number
  /** Fixed step, in seconds. */
  dt: number
  /** Real-time accumulator; whole steps are drained from it. */
  accumulator: number
  rng: SimRng
  /** The single ordered command FIFO. */
  commandQueue: Command[]
  /** The placement grid: tile pitch, extent, and tile occupancy. */
  grid: SimGrid
  /** Ordered chain of buildings. */
  buildings: SimBuilding[]
  /** Resource patches seeded on the map; extractors draw from an adjacent one. */
  patches: SimPatch[]
  /** The chain's building ids, in order (the shared template all chains use). */
  chain: ContentId[]
  /**
   * The player-assignable chain instances, one per parallel group, built from
   * `chain` × `groupCount`. Each holds its buildings' flat indices and its
   * target headcount. This is the assignment unit; `laborPool` stays the one
   * shared pool of colonists not currently serving any chain.
   */
  chains: SimChain[]
  units: SimUnit[]
  /**
   * The single shared labor pool: the ids of colonists NOT currently serving any
   * chain (each has `chainId === -1` and idles at the rally point). There is
   * exactly ONE pool — the arbiter draws the lowest-id colonist from here when a
   * chain is under target and returns a released colonist here — never a second
   * or per-purpose pool. Per-chain targets live on `chains[i].target`, not here.
   */
  laborPool: { colonistIds: number[] }
  /** Reserved transfers awaiting the commit phase. */
  pendingTransfers: Transfer[]
  legs: CompletedLeg[]
  /** Fixed rally point idle colonists gather at, so wasted labor is legible. */
  poolPoint: Vec2
  /** Number of parallel chain groups; the flat chain length times this is buildings.length. */
  groupCount: number
  /** Latest events-stream roll; advances the events cursor each tick. */
  lastEventRoll: number
  /** Total commands drained across all ticks. */
  drainedCommandCount: number
}

/** The read-only geometry of a grid — everything but the occupancy Record. Both
 * SimGrid and the render-side GridView satisfy it, so the tile math is shared. */
export interface GridGeometry {
  tile: number
  cols: number
  rows: number
}

/** Tile key for the occupancy Record. */
export function tileKey(tx: number, ty: number): string {
  return `${tx},${ty}`
}

/** World center a building placed on `tile` sits at. Fixed sim-space mapping. */
export function tileCenter(grid: GridGeometry, tile: Tile): Vec2 {
  return { x: tile.tx * grid.tile, y: tile.ty * grid.tile }
}

/** Nearest tile to a world point. The inverse of tileCenter, used by input. */
export function worldToTile(grid: GridGeometry, p: Vec2): Tile {
  return { tx: Math.round(p.x / grid.tile), ty: Math.round(p.y / grid.tile) }
}

/** True when a tile index lies inside the placeable extent. */
export function tileInBounds(grid: GridGeometry, tile: Tile): boolean {
  return (
    tile.tx >= 0 && tile.tx < grid.cols && tile.ty >= 0 && tile.ty < grid.rows
  )
}

/** The box a BUILDING_SIZE building occupies when placed centered on `tile`. */
export function buildingBoxAt(grid: GridGeometry, tile: Tile): Aabb {
  const c = tileCenter(grid, tile)
  return makeAabb(
    c.x - BUILDING_SIZE / 2,
    c.y - BUILDING_SIZE / 2,
    BUILDING_SIZE,
    BUILDING_SIZE,
  )
}

/** Every tile whose center point falls within `box` — the building's footprint. */
export function coveredTiles(grid: GridGeometry, box: Aabb): Tile[] {
  const t = grid.tile
  const tiles: Tile[] = []
  for (
    let tx = Math.ceil(box.min.x / t);
    tx <= Math.floor(box.max.x / t);
    tx++
  ) {
    for (
      let ty = Math.ceil(box.min.y / t);
      ty <= Math.floor(box.max.y / t);
      ty++
    ) {
      tiles.push({ tx, ty })
    }
  }
  return tiles
}

/**
 * True when a building could occupy `tile`: in bounds and every tile its
 * footprint would cover is free. This is the geometry half of placement
 * validation, shared by the place_building drain (which also checks the content
 * id) and the render placement affordance, so the ghost's accept/reject preview
 * matches what the drain will actually decide.
 */
export function isTileAreaFree(
  grid: GridGeometry & { occupied: Readonly<Record<string, boolean>> },
  tile: Tile,
): boolean {
  if (!tileInBounds(grid, tile)) return false
  for (const t of coveredTiles(grid, buildingBoxAt(grid, tile))) {
    // The whole footprint must sit inside the extent, not just the target tile:
    // a building centered near the edge would otherwise spill its box past the
    // grid and mark out-of-range tiles occupied.
    if (!tileInBounds(grid, t)) return false
    if (grid.occupied[tileKey(t.tx, t.ty)]) return false
  }
  return true
}

/**
 * True when any tile of box `a` is orthogonally adjacent to a tile of box `b` —
 * a topology/occupancy test over the grid tiles, not a distance metric (steering
 * distance stays Euclidean). Used for edge-to-edge footprint adjacency. Note:
 * for overlapping boxes this also returns true (an interior tile's neighbour is
 * covered too), so it is not a strict disjoint-adjacency test; the sim never
 * overlaps two footprints (grid occupancy prevents it), so that case does not
 * arise at runtime.
 */
export function boxesOrthogonallyAdjacent(
  grid: GridGeometry,
  a: Aabb,
  b: Aabb,
): boolean {
  return boxAdjacentToTileSet(grid, a, tileKeySet(grid, b))
}

/** The set of tile keys a box's footprint covers. Hoist this once when a box is
 * tested for adjacency against several others so the set is not rebuilt per call. */
export function tileKeySet(grid: GridGeometry, box: Aabb): Set<string> {
  return new Set(coveredTiles(grid, box).map((t) => tileKey(t.tx, t.ty)))
}

/** True when any tile of `a`'s footprint is orthogonally adjacent to a tile in
 * `bTiles` (a precomputed footprint tile-key set). The set half of
 * boxesOrthogonallyAdjacent, split out so a caller comparing many boxes to one
 * box builds that box's set once. */
export function boxAdjacentToTileSet(
  grid: GridGeometry,
  a: Aabb,
  bTiles: ReadonlySet<string>,
): boolean {
  for (const t of coveredTiles(grid, a)) {
    if (
      bTiles.has(tileKey(t.tx + 1, t.ty)) ||
      bTiles.has(tileKey(t.tx - 1, t.ty)) ||
      bTiles.has(tileKey(t.tx, t.ty + 1)) ||
      bTiles.has(tileKey(t.tx, t.ty - 1))
    ) {
      return true
    }
  }
  return false
}

/** Build a placed building from its footprint box, with access slots to its
 * right (so a hauler arriving from the left must round the box to reach them). */
export function makeBuilding(
  id: ContentId,
  slotCount: number,
  box: Aabb,
): SimBuilding {
  const cy = (box.min.y + box.max.y) / 2
  const slots: Slot[] = []
  for (let i = 0; i < slotCount; i++) {
    slots.push({
      pos: {
        x: box.max.x + SLOT_GAP,
        y: cy + (i - (slotCount - 1) / 2) * SLOT_PITCH,
      },
      occupant: null,
    })
  }
  return { id, box, slots, inventory: {}, cooldown: 0 }
}

// Longest overflow-queue rank that gets its own spot. Past this the line would
// run rightward into the next chain building at the starter spacing, so higher
// ranks share the last spot rather than targeting a point inside a downstream
// box (steering never reaches an in-box target and would mill against the wrong
// building). Under arbitrary player packing a building placed inside even this
// capped span is the player's layout choice; the cap bounds the line's reach.
const MAX_QUEUE_RANK = 7

/**
 * Waiting spot for the colonist ranked `rank` in a building's overflow queue.
 * The line extends rightward past the access slots so a queue reads as a legible
 * row rather than a stack, capped at MAX_QUEUE_RANK so it never runs into the
 * downstream building. Positions are fixed sim-world coordinates and depend only
 * on the building and rank, so they stay deterministic.
 */
export function queueWaitPos(building: SimBuilding, rank: number): Vec2 {
  const capped = Math.min(rank, MAX_QUEUE_RANK)
  return {
    x: building.box.max.x + SLOT_GAP + QUEUE_STRIDE * (capped + 1),
    y: (building.box.min.y + building.box.max.y) / 2,
  }
}

/**
 * Pool spot for a colonist, fanned out from the rally point on a fixed grid
 * keyed by unit id so pooled squares spread out (unit-unit collision is out of
 * scope) instead of stacking on one pixel. Deterministic in the unit id.
 */
export function poolSpot(poolPoint: Vec2, unitId: number): Vec2 {
  const col = unitId % POOL_COLS
  const row = Math.floor(unitId / POOL_COLS)
  return {
    x: poolPoint.x + col * POOL_STRIDE,
    y: poolPoint.y + row * POOL_STRIDE,
  }
}

export interface CreateStateOpts {
  masterSeed?: number
  /** Number of parallel chain groups to lay out. Defaults to one. */
  groupCount?: number
}

/**
 * Build a fresh state and queue the starter chain as place_building commands —
 * `groupCount` left-to-right rows, groups stacked vertically — plus an empty
 * placement grid. Buildings enter the sim ONLY by draining those commands (the
 * first tick applies them), the same command-only path colonists take via
 * spawn_colonist; nothing is constructed inline. A resource patch is seeded for
 * each fromPatch extractor as map data (not a command), adjacent to the
 * extractor's tile. Given the same seed, content and group count this always
 * produces an identical starting command log and patch layout: no clock, no
 * Math.random, no dependence on the render viewport. Each starter building's
 * fixed world center divides GRID_TILE, so it lands on an integer tile at
 * exactly its former coordinates.
 */
export function createSimState(
  content: Content,
  chainIds: ContentId[],
  opts: CreateStateOpts = {},
): SimState {
  const masterSeed = (opts.masterSeed ?? 1) >>> 0
  // Clamp to a finite integer >= 1. Math.floor alone lets NaN through (which
  // seeds an empty scene, no throw) and Infinity through (which would spin the
  // group loop forever); a non-finite groupCount falls back to one group.
  const rawGroupCount = opts.groupCount ?? 1
  const groupCount = Number.isFinite(rawGroupCount)
    ? Math.max(1, Math.floor(rawGroupCount))
    : 1

  // Validate the chain up front so a mis-authored chain fails fast+loud here
  // rather than silently later: validateChain checks both that every id resolves
  // and that adjacent recipes connect (upstream output === downstream input), so
  // a disconnected chain — which would derive zero pull edges and haul nothing —
  // throws instead of no-op-rejecting during the drain. Then queue one placement
  // per starter building in group-major, chain-minor order (the order buildings
  // must sit in state.buildings for the pull-walk's index math).
  validateChain(content, chainIds)
  const grid: SimGrid = {
    tile: GRID_TILE,
    cols: GRID_COLS,
    rows: GRID_ROWS,
    occupied: {},
  }
  const commandQueue: Command[] = []
  // Patches are world/map data seeded here (like grid/poolPoint), NOT via a
  // player command: a patch is not player intent, so the command-only entry
  // rule does not require one. Buildings still enter only via place_building.
  // This seeding writes the occupancy slice at construction, before any tick has
  // run, so it does not contend with the phase-1 place drain that owns it
  // thereafter.
  const patches: SimPatch[] = []
  for (let g = 0; g < groupCount; g++) {
    const ty = (CHAIN_ROW_Y + g * GROUP_ROW_SPACING) / GRID_TILE
    for (let i = 0; i < chainIds.length; i++) {
      const id = chainIds[i]
      const def = content.buildings.get(id)
      if (def === undefined) {
        throw new Error(`createSimState: chain building ${id} not in content`)
      }
      const tx = (CHAIN_ORIGIN_X + i * CHAIN_SPACING_X) / GRID_TILE
      commandQueue.push(placeBuilding(id, { tx, ty }))

      // A fromPatch extractor is the chain head: seed a patch yielding its
      // output orthogonally adjacent (one footprint above), so it produces
      // because a patch is adjacent rather than from nothing.
      const recipe = content.recipes.get(def.recipe)
      if (recipe?.fromPatch && recipe.output !== undefined) {
        const patchDef = [...content.patches.values()].find(
          (p) => p.item === recipe.output,
        )
        if (patchDef === undefined) {
          throw new Error(
            `createSimState: no patch yields ${recipe.output} for extractor ${id}`,
          )
        }
        const patchTile = { tx, ty: ty - PATCH_OFFSET_TILES }
        if (!isTileAreaFree(grid, patchTile)) {
          throw new Error(
            `createSimState: seeded patch for extractor ${id} is out of bounds or overlaps an occupied tile`,
          )
        }
        const patchBox = buildingBoxAt(grid, patchTile)
        patches.push({
          id: patchDef.id,
          box: patchBox,
          item: patchDef.item,
          extractionSlots: extractionSlotCount(patchDef),
        })
        for (const t of coveredTiles(grid, patchBox)) {
          grid.occupied[tileKey(t.tx, t.ty)] = true
        }
      }
    }
  }

  // The chain instances mirror the group-major placement order above: group g's
  // buildings occupy flat indices g*chainLen .. g*chainLen+chainLen-1 in
  // state.buildings (the same order the placements drain), so a chain's
  // buildingIdx is derivable here before any building exists. target starts at 0
  // — no colonist serves a chain until an assign_labor command sets its target,
  // so a chain with no assignment moves nothing.
  const chainLen = chainIds.length
  const chains: SimChain[] = []
  for (let g = 0; g < groupCount; g++) {
    const buildingIdx: number[] = []
    for (let i = 0; i < chainLen; i++) buildingIdx.push(g * chainLen + i)
    chains.push({ buildingIdx, target: 0 })
  }

  const streams: Record<string, RngState> = {}
  for (const name of RNG_STREAMS) {
    streams[name] = makeRngState(subSeed(masterSeed, name))
  }

  return {
    tick: 0,
    dt: SIM_DT,
    accumulator: 0,
    rng: { masterSeed, streams },
    commandQueue,
    grid,
    buildings: [],
    patches,
    chain: [...chainIds],
    chains,
    units: [],
    laborPool: { colonistIds: [] },
    pendingTransfers: [],
    legs: [],
    poolPoint: { ...POOL_POINT },
    groupCount,
    drainedCommandCount: 0,
    lastEventRoll: 0,
  }
}
